import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon, HomeIcon, CurrencyDollarIcon, StarIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const BecomeHostPage: React.FC = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: <HomeIcon className="h-8 w-8 text-airbnb-red" />,
      title: "Host your space",
      description: "Share your home, room, or unique space and earn extra income as a host."
    },
    {
      icon: <CurrencyDollarIcon className="h-8 w-8 text-airbnb-red" />,
      title: "Earn money",
      description: "Set your own schedule, prices, and requirements for guests."
    },
    {
      icon: <StarIcon className="h-8 w-8 text-airbnb-red" />,
      title: "We've got your back",
      description: "$1M USD in property damage protection and liability insurance."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="relative">
        <div className="absolute inset-0">
          <img
            className="h-full w-full object-cover"
            src="https://picsum.photos/1920/1080?random=1"
            alt="Become a host"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
              Open your door to
              <span className="block text-airbnb-red">hosting</span>
            </h1>
            <p className="mt-6 max-w-lg mx-auto text-xl text-gray-200">
              Join us. We'll help you every step of the way.
            </p>
            <div className="mt-10">
              <button
                onClick={() => navigate('/host/onboarding')}
                className="inline-flex items-center px-8 py-4 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-airbnb-red hover:bg-red-600 transition-colors duration-200"
              >
                Get started
                <ChevronRightIcon className="ml-2 h-5 w-5" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Why host on Airbnb?
            </h2>
            <p className="mt-4 text-lg leading-6 text-gray-500">
              Join millions of hosts on Airbnb.
            </p>
          </div>

          <div className="mt-20">
            <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-3">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="text-center"
                >
                  <div className="flex items-center justify-center">
                    {benefit.icon}
                  </div>
                  <h3 className="mt-6 text-lg font-medium text-gray-900">
                    {benefit.title}
                  </h3>
                  <p className="mt-2 text-base text-gray-500">
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
              Ready to earn?
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Start your hosting journey today.
            </p>
            <button
              onClick={() => navigate('/host/onboarding')}
              className="mt-8 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-airbnb-red hover:bg-red-600 transition-colors duration-200"
            >
              Get started now
              <ChevronRightIcon className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BecomeHostPage;